<template>
    <div class="shopify-filter-bar" ref="root">
        <div class="shopify-filter-bar__control" @click="onControlClick">
            <span class="shopify-filter-bar__search-icon">
                <i class="fas fa-search"></i>
            </span>

            <div
                v-for="pill in visiblePills"
                :key="pill.key"
                class="shopify-filter-bar__pill"
                @click.stop="editField(pill.key)"
            >
                <span class="shopify-filter-bar__pill-label">{{ pill.label }}</span>
                <span class="shopify-filter-bar__pill-op">{{ pill.operatorLabel }}</span>
                <span class="shopify-filter-bar__pill-value">{{ pill.valueLabel }}</span>
                <button
                    type="button"
                    class="shopify-filter-bar__pill-remove"
                    :aria-label="'Remove ' + pill.label + ' filter'"
                    @click.stop="removeField(pill.key)"
                >
                    &times;
                </button>
            </div>

            <button
                v-if="showAddButton"
                type="button"
                class="shopify-filter-bar__add"
                title="Add filter"
                aria-label="Add filter"
                @click.stop="openFieldMenu"
            >
                <i class="fas fa-plus"></i>
            </button>

            <div class="shopify-filter-bar__input-wrap" @click="onInputWrapClick">
                <template v-if="activeFieldKey && activeField">
                    <span class="shopify-filter-bar__draft" @click.stop>
                        <span class="shopify-filter-bar__draft-label">{{ activeField.label }}</span>
                        <span class="shopify-filter-bar__draft-op">{{ draftOperatorLabel }}</span>
                    </span>
                    <input
                        v-if="activeFieldType === 'text' || isSelectLikeType"
                        ref="draftInput"
                        type="text"
                        class="shopify-filter-bar__input shopify-filter-bar__input--draft"
                        :value="optionSearch"
                        :placeholder="isAsyncSelectType ? 'Search...' : ''"
                        @click.stop
                        @input="onDraftOptionSearchInput"
                        @keydown.enter.prevent="onDraftEnter"
                        @keydown.esc.prevent="cancelEdit"
                    />
                    <span v-else class="shopify-filter-bar__draft-spacer"></span>
                </template>
                <input
                    v-else
                    ref="searchInput"
                    type="search"
                    class="shopify-filter-bar__input"
                    :value="search"
                    :placeholder="placeholder"
                    @click.stop
                    @focus="onSearchFocus"
                    @input="onSearchInput"
                    @keydown.esc.prevent="fieldMenuOpen = false"
                />

                <div
                    v-if="fieldMenuOpen"
                    class="shopify-filter-bar__menu shopify-filter-bar__menu--fields"
                    @click.stop
                >
                    <button
                        v-for="field in availableFields"
                        :key="field.key"
                        type="button"
                        class="shopify-filter-bar__menu-item"
                        @click="startField(field.key)"
                    >
                        {{ field.label }}
                    </button>
                    <div v-if="!availableFields.length" class="shopify-filter-bar__menu-empty">
                        All filters applied
                    </div>
                </div>

                <div
                    v-if="activeFieldKey && activeField && isSelectLikeType"
                    class="shopify-filter-bar__menu shopify-filter-bar__menu--values"
                    @click.stop
                >
                    <div
                        ref="optionList"
                        class="shopify-filter-bar__option-list"
                        @scroll.passive="onOptionListScroll"
                    >
                        <label
                            v-for="option in displayedOptions"
                            :key="String(option.id)"
                            class="shopify-filter-bar__option"
                        >
                            <input
                                type="checkbox"
                                :checked="isDraftSelected(option.id)"
                                @change="toggleDraftValue(option.id, $event.target.checked)"
                            />
                            <span>{{ option.label }}</span>
                        </label>
                        <div v-if="asyncLoading" class="shopify-filter-bar__menu-empty">
                            Loading...
                        </div>
                        <div v-else-if="!displayedOptions.length" class="shopify-filter-bar__menu-empty">
                            No options
                        </div>
                    </div>
                    <div class="shopify-filter-bar__operators">
                        <button
                            type="button"
                            class="shopify-filter-bar__operator"
                            :class="{ 'is-active': draftOperator === 'is' }"
                            @click="setDraftOperator('is')"
                        >
                            <i v-if="draftOperator === 'is'" class="fas fa-check"></i>
                            Is
                        </button>
                        <button
                            type="button"
                            class="shopify-filter-bar__operator"
                            :class="{ 'is-active': draftOperator === 'is_not' }"
                            @click="setDraftOperator('is_not')"
                        >
                            <i v-if="draftOperator === 'is_not'" class="fas fa-check"></i>
                            Is not
                        </button>
                    </div>
                </div>

                <div
                    v-if="activeFieldKey && activeField && activeFieldType === 'number'"
                    class="shopify-filter-bar__menu shopify-filter-bar__menu--range"
                    @click.stop
                >
                    <div class="shopify-filter-bar__range-row">
                        <label>
                            From
                            <input
                                ref="draftInput"
                                type="number"
                                step="any"
                                class="shopify-filter-bar__range-input"
                                :value="draftFrom"
                                @input="onRangeFromInput($event.target.value)"
                                @keydown.enter.prevent="finalizeDraftAndContinue"
                                @keydown.esc.prevent="cancelEdit"
                            />
                        </label>
                        <span class="shopify-filter-bar__range-sep">–</span>
                        <label>
                            To
                            <input
                                type="number"
                                step="any"
                                class="shopify-filter-bar__range-input"
                                :value="draftTo"
                                @input="onRangeToInput($event.target.value)"
                                @keydown.enter.prevent="finalizeDraftAndContinue"
                                @keydown.esc.prevent="cancelEdit"
                            />
                        </label>
                    </div>
                </div>

                <div
                    v-if="activeFieldKey && activeField && activeFieldType === 'date'"
                    class="shopify-filter-bar__menu shopify-filter-bar__menu--date"
                    @click.stop
                >
                    <div class="shopify-filter-bar__option-list">
                        <button
                            v-for="preset in datePresets"
                            :key="preset.id"
                            type="button"
                            class="shopify-filter-bar__menu-item shopify-filter-bar__date-preset"
                            :class="{ 'is-active': draftPreset === preset.id }"
                            @click="selectDatePreset(preset.id)"
                        >
                            <i v-if="draftPreset === preset.id" class="fas fa-check"></i>
                            <span v-else class="shopify-filter-bar__check-spacer"></span>
                            <span>{{ preset.label }}</span>
                        </button>
                    </div>

                    <div v-if="draftPreset === 'custom_date'" class="shopify-filter-bar__custom-date">
                        <label>
                            Date
                            <input
                                ref="draftInput"
                                type="date"
                                class="shopify-filter-bar__range-input"
                                :value="draftFrom"
                                @input="onCustomDateInput($event.target.value)"
                                @keydown.enter.prevent="finalizeDraftAndContinue"
                                @keydown.esc.prevent="cancelEdit"
                            />
                        </label>
                    </div>

                    <div v-if="draftPreset === 'custom_range'" class="shopify-filter-bar__custom-date">
                        <div class="shopify-filter-bar__range-row">
                            <label>
                                From
                                <input
                                    ref="draftInput"
                                    type="date"
                                    class="shopify-filter-bar__range-input"
                                    :value="draftFrom"
                                    @input="onCustomRangeFromInput($event.target.value)"
                                    @keydown.enter.prevent="finalizeDraftAndContinue"
                                    @keydown.esc.prevent="cancelEdit"
                                />
                            </label>
                            <span class="shopify-filter-bar__range-sep">–</span>
                            <label>
                                To
                                <input
                                    type="date"
                                    class="shopify-filter-bar__range-input"
                                    :value="draftTo"
                                    @input="onCustomRangeToInput($event.target.value)"
                                    @keydown.enter.prevent="finalizeDraftAndContinue"
                                    @keydown.esc.prevent="cancelEdit"
                                />
                            </label>
                        </div>
                    </div>

                    <div class="shopify-filter-bar__operators">
                        <button
                            type="button"
                            class="shopify-filter-bar__operator"
                            :class="{ 'is-active': draftOperator === 'is' }"
                            @click="setDraftOperator('is')"
                        >
                            <i v-if="draftOperator === 'is'" class="fas fa-check"></i>
                            Is
                        </button>
                        <button
                            type="button"
                            class="shopify-filter-bar__operator"
                            :class="{ 'is-active': draftOperator === 'is_not' }"
                            @click="setDraftOperator('is_not')"
                        >
                            <i v-if="draftOperator === 'is_not'" class="fas fa-check"></i>
                            Is not
                        </button>
                    </div>
                </div>

                <div
                    v-if="activeFieldKey && activeField && activeFieldType === 'text'"
                    class="shopify-filter-bar__menu shopify-filter-bar__menu--text"
                    @click.stop
                >
                    <div class="shopify-filter-bar__operators">
                        <button
                            type="button"
                            class="shopify-filter-bar__operator"
                            :class="{ 'is-active': draftOperator === 'is' }"
                            @click="setDraftOperator('is')"
                        >
                            <i v-if="draftOperator === 'is'" class="fas fa-check"></i>
                            Is
                        </button>
                        <button
                            type="button"
                            class="shopify-filter-bar__operator"
                            :class="{ 'is-active': draftOperator === 'is_not' }"
                            @click="setDraftOperator('is_not')"
                        >
                            <i v-if="draftOperator === 'is_not'" class="fas fa-check"></i>
                            Is not
                        </button>
                    </div>
                    <div class="shopify-filter-bar__text-hint">Type a value and press Enter</div>
                </div>
            </div>

            <button
                v-if="clearable"
                type="button"
                class="shopify-filter-bar__clear-x"
                title="Clear filters"
                aria-label="Clear filters"
                @click.stop="$emit('clear')"
            >
                <i class="fas fa-times"></i>
            </button>
        </div>
    </div>
</template>

<script>
const DATE_PRESETS = [
    { id: 'today', label: 'Today' },
    { id: 'last_7_days', label: 'Last 7 days' },
    { id: 'last_30_days', label: 'Last 30 days' },
    { id: 'last_90_days', label: 'Last 90 days' },
    { id: 'last_12_months', label: 'Last 12 months' },
    { id: 'custom_date', label: 'Custom date' },
    { id: 'custom_range', label: 'Custom range' },
]

function emptyStateForType(type) {
    if (type === 'date') {
        return { type: 'date', preset: '', from: '', to: '', operator: 'is' }
    }
    if (type === 'number') {
        return { type: 'number', from: '', to: '', operator: 'between' }
    }
    if (type === 'text') {
        return { type: 'text', value: '', operator: 'is' }
    }
    if (type === 'async-select') {
        return { type: 'async-select', values: [], operator: 'is', valueLabels: {} }
    }
    return { type: 'select', values: [], operator: 'is' }
}

function hasFilterValue(state, type) {
    if (!state) {
        return false
    }
    const resolvedType = type || state.type || 'select'
    if (resolvedType === 'date') {
        const preset = state.preset || ''
        if (['today', 'last_7_days', 'last_30_days', 'last_90_days', 'last_12_months'].includes(preset)) {
            return true
        }
        if (preset === 'custom_date') {
            return state.from !== '' && state.from != null
        }
        if (preset === 'custom_range') {
            return (state.from !== '' && state.from != null) || (state.to !== '' && state.to != null)
        }
        return (state.from !== '' && state.from != null) || (state.to !== '' && state.to != null)
    }
    if (resolvedType === 'number') {
        return (state.from !== '' && state.from != null) || (state.to !== '' && state.to != null)
    }
    if (resolvedType === 'text') {
        return state.value != null && String(state.value).trim() !== ''
    }
    return Array.isArray(state.values) && state.values.length > 0
}

function datePresetLabel(preset) {
    const match = DATE_PRESETS.find((item) => item.id === preset)
    return match ? match.label : preset
}

export default {
    name: 'ShopifyStyleFilterBar',

    props: {
        search: {
            type: String,
            default: '',
        },
        placeholder: {
            type: String,
            default: 'Filter orders',
        },
        clearable: {
            type: Boolean,
            default: false,
        },
        /**
         * [{ key, label, type?: 'select'|'number'|'text'|'date', options?: [{ id, label }] }]
         */
        fields: {
            type: Array,
            required: true,
        },
        /**
         * { [key]: { type, values?, operator?, from?, to?, value? } }
         */
        value: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            fieldMenuOpen: false,
            activeFieldKey: null,
            draftValues: [],
            draftOperator: 'is',
            draftFrom: '',
            draftTo: '',
            draftPreset: '',
            optionSearch: '',
            searchDebounceTimer: null,
            rangeDebounceTimer: null,
            asyncSearchDebounceTimer: null,
            datePresets: DATE_PRESETS,
            asyncOptions: [],
            asyncPage: 1,
            asyncHasMore: false,
            asyncLoading: false,
            optionLabelCache: {},
        }
    },

    computed: {
        availableFields() {
            return this.fields.filter((field) => {
                if (this.activeFieldKey && field.key === this.activeFieldKey) {
                    return false
                }
                return !hasFilterValue(this.value[field.key], field.type)
            })
        },
        showAddButton() {
            return this.visiblePills.length > 0 && this.availableFields.length > 0 && !this.activeFieldKey
        },
        activePills() {
            return this.fields
                .map((field) => {
                    const state = this.value[field.key]
                    if (!hasFilterValue(state, field.type)) {
                        return null
                    }
                    return this.buildPill(field, state)
                })
                .filter(Boolean)
        },
        visiblePills() {
            if (!this.activeFieldKey) {
                return this.activePills
            }
            return this.activePills.filter((pill) => pill.key !== this.activeFieldKey)
        },
        activeField() {
            return this.fields.find((field) => field.key === this.activeFieldKey) || null
        },
        activeFieldType() {
            return (this.activeField && this.activeField.type) || 'select'
        },
        isAsyncSelectType() {
            return this.activeFieldType === 'async-select'
        },
        isSelectLikeType() {
            return this.activeFieldType === 'select' || this.activeFieldType === 'async-select'
        },
        draftOperatorLabel() {
            if (this.activeFieldType === 'number') {
                return 'is between'
            }
            if (this.activeFieldType === 'date') {
                if (this.draftPreset === 'custom_range') {
                    return this.draftOperator === 'is_not' ? 'is not between' : 'is between'
                }
                return this.draftOperator === 'is_not' ? 'is not' : 'is'
            }
            return this.draftOperator === 'is_not' ? 'is not' : 'is'
        },
        filteredOptions() {
            if (!this.activeField) {
                return []
            }

            const options = this.activeField.options || []
            const needle = this.optionSearch.trim().toLowerCase()
            if (!needle) {
                return options
            }

            return options.filter((option) => String(option.label).toLowerCase().includes(needle))
        },
        displayedOptions() {
            if (this.isAsyncSelectType) {
                return this.asyncOptions
            }
            return this.filteredOptions
        },
    },

    mounted() {
        document.addEventListener('click', this.handleDocumentClick)
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick)
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }
        if (this.rangeDebounceTimer) {
            clearTimeout(this.rangeDebounceTimer)
        }
        if (this.asyncSearchDebounceTimer) {
            clearTimeout(this.asyncSearchDebounceTimer)
        }
    },

    methods: {
        buildPill(field, state) {
            const type = field.type || state.type || 'select'

            if (type === 'date') {
                const operator = state.operator === 'is_not' ? 'is_not' : 'is'
                const preset = state.preset || ''

                if (['today', 'last_7_days', 'last_30_days', 'last_90_days', 'last_12_months'].includes(preset)) {
                    return {
                        key: field.key,
                        label: field.label,
                        operatorLabel: operator === 'is_not' ? 'is not' : 'is',
                        valueLabel: datePresetLabel(preset),
                    }
                }

                if (preset === 'custom_date') {
                    return {
                        key: field.key,
                        label: field.label,
                        operatorLabel: operator === 'is_not' ? 'is not' : 'is',
                        valueLabel: state.from ? String(state.from) : '…',
                    }
                }

                const from = state.from !== '' && state.from != null ? String(state.from) : '…'
                const to = state.to !== '' && state.to != null ? String(state.to) : '…'
                return {
                    key: field.key,
                    label: field.label,
                    operatorLabel: operator === 'is_not' ? 'is not between' : 'is between',
                    valueLabel: from + ' – ' + to,
                }
            }

            if (type === 'number') {
                const from = state.from !== '' && state.from != null ? String(state.from) : '…'
                const to = state.to !== '' && state.to != null ? String(state.to) : '…'
                return {
                    key: field.key,
                    label: field.label,
                    operatorLabel: 'is between',
                    valueLabel: from + ' – ' + to,
                }
            }

            if (type === 'text') {
                return {
                    key: field.key,
                    label: field.label,
                    operatorLabel: state.operator === 'is_not' ? 'is not' : 'is',
                    valueLabel: String(state.value),
                }
            }

            const values = Array.isArray(state.values) ? state.values : []
            const labels = values.map((id) => {
                const cached = this.optionLabelCache[String(id)]
                if (cached) {
                    return cached
                }
                if (state.valueLabels && state.valueLabels[String(id)]) {
                    return state.valueLabels[String(id)]
                }
                const option = (field.options || []).find((item) => String(item.id) === String(id))
                return option ? option.label : String(id)
            })

            return {
                key: field.key,
                label: field.label,
                operatorLabel: state.operator === 'is_not' ? 'is not' : 'is',
                valueLabel: labels.join(', '),
            }
        },

        handleDocumentClick(event) {
            if (!this.$refs.root || this.$refs.root.contains(event.target)) {
                return
            }

            this.closeMenus()
        },

        closeMenus() {
            this.fieldMenuOpen = false
            if (this.activeFieldKey) {
                this.activeFieldKey = null
                this.resetDraft()
            }
        },

        resetDraft() {
            this.draftValues = []
            this.draftOperator = 'is'
            this.draftFrom = ''
            this.draftTo = ''
            this.draftPreset = ''
            this.optionSearch = ''
            this.asyncOptions = []
            this.asyncPage = 1
            this.asyncHasMore = false
            this.asyncLoading = false
        },

        onControlClick() {
            if (this.activeFieldKey) {
                this.finalizeDraftAndContinue()
                return
            }
            this.focusSearchInput()
        },

        onInputWrapClick() {
            if (this.activeFieldKey) {
                this.finalizeDraftAndContinue()
                return
            }
            this.focusSearchInput()
        },

        onDraftEnter() {
            if (this.activeFieldType === 'text') {
                const value = this.optionSearch.trim()
                this.emitChange(this.activeFieldKey, {
                    type: 'text',
                    value,
                    operator: this.draftOperator === 'is_not' ? 'is_not' : 'is',
                })
                if (!value) {
                    this.cancelEdit()
                    return
                }
            }
            this.finalizeDraftAndContinue()
        },

        finalizeDraftAndContinue() {
            const key = this.activeFieldKey
            if (!key) {
                return
            }

            const field = this.activeField
            const type = (field && field.type) || 'select'

            if (type === 'text') {
                const value = this.optionSearch.trim()
                this.emitChange(key, {
                    type: 'text',
                    value,
                    operator: this.draftOperator === 'is_not' ? 'is_not' : 'is',
                })
            } else if (type === 'date') {
                if (!hasFilterValue({
                    type: 'date',
                    preset: this.draftPreset,
                    from: this.draftFrom,
                    to: this.draftTo,
                    operator: this.draftOperator,
                }, 'date')) {
                    this.emitChange(key, emptyStateForType('date'))
                } else {
                    this.commitDateDraft()
                }
            } else if (type === 'number') {
                if (!hasFilterValue({ from: this.draftFrom, to: this.draftTo }, 'number')) {
                    this.emitChange(key, emptyStateForType('number'))
                } else {
                    this.commitRangeDraft()
                }
            } else if (!this.draftValues.length) {
                this.emitChange(key, emptyStateForType('select'))
            }

            this.activeFieldKey = null
            this.resetDraft()
            this.fieldMenuOpen = false

            this.$nextTick(() => {
                if (this.availableFields.length) {
                    this.openFieldMenu()
                } else {
                    this.focusSearchInput()
                }
            })
        },

        focusSearchInput() {
            this.$nextTick(() => {
                const input = this.$refs.searchInput
                if (input && typeof input.focus === 'function') {
                    input.focus()
                }
            })
        },

        focusDraftInput() {
            this.$nextTick(() => {
                const input = this.$refs.draftInput
                if (input && typeof input.focus === 'function') {
                    input.focus()
                }
            })
        },

        onSearchFocus() {
            if (this.activeFieldKey) {
                return
            }
            this.openFieldMenu()
        },

        onSearchInput(event) {
            const next = event.target.value
            if (this.searchDebounceTimer) {
                clearTimeout(this.searchDebounceTimer)
            }
            this.searchDebounceTimer = setTimeout(() => {
                this.$emit('update:search', next)
            }, 500)
        },

        openFieldMenu() {
            if (!this.availableFields.length && !this.activeFieldKey) {
                this.fieldMenuOpen = false
                this.focusSearchInput()
                return
            }

            this.activeFieldKey = null
            this.resetDraft()
            this.fieldMenuOpen = true
            this.focusSearchInput()
        },

        isDraftSelected(id) {
            return this.draftValues.some((value) => String(value) === String(id))
        },

        startField(key) {
            this.fieldMenuOpen = false
            const field = this.fields.find((item) => item.key === key)
            const type = (field && field.type) || 'select'
            const state = emptyStateForType(type)
            if (type === 'date') {
                state.preset = 'today'
            }
            this.openEditor(key, state)
            if (type === 'date') {
                this.selectDatePreset('today')
            }
        },

        editField(key) {
            const field = this.fields.find((item) => item.key === key)
            const type = (field && field.type) || 'select'
            const state = this.value[key] || emptyStateForType(type)
            this.openEditor(key, state)
        },

        openEditor(key, state) {
            const field = this.fields.find((item) => item.key === key)
            const type = (field && field.type) || state.type || 'select'

            this.activeFieldKey = key
            this.fieldMenuOpen = false
            this.optionSearch = ''

            if (type === 'date') {
                this.draftPreset = state.preset || (state.from || state.to ? 'custom_range' : 'today')
                this.draftFrom = state.from != null ? state.from : ''
                this.draftTo = state.to != null ? state.to : ''
                this.draftOperator = state.operator === 'is_not' ? 'is_not' : 'is'
                this.draftValues = []
            } else if (type === 'number') {
                this.draftPreset = ''
                this.draftFrom = state.from != null ? state.from : ''
                this.draftTo = state.to != null ? state.to : ''
                this.draftOperator = 'between'
                this.draftValues = []
            } else if (type === 'text') {
                this.draftPreset = ''
                this.draftFrom = ''
                this.draftTo = ''
                this.draftOperator = state.operator === 'is_not' ? 'is_not' : 'is'
                this.draftValues = []
                this.optionSearch = state.value != null ? String(state.value) : ''
            } else {
                this.draftPreset = ''
                this.draftFrom = ''
                this.draftTo = ''
                this.draftOperator = state.operator === 'is_not' ? 'is_not' : 'is'
                this.draftValues = Array.isArray(state.values) ? [...state.values] : []
                if (state.valueLabels && typeof state.valueLabels === 'object') {
                    Object.keys(state.valueLabels).forEach((id) => {
                        this.$set(this.optionLabelCache, String(id), state.valueLabels[id])
                    })
                }
            }

            this.focusDraftInput()

            if (type === 'async-select') {
                this.loadAsyncOptions(true)
            }
        },

        onDraftOptionSearchInput(event) {
            this.optionSearch = event.target.value
            if (!this.isAsyncSelectType) {
                return
            }
            if (this.asyncSearchDebounceTimer) {
                clearTimeout(this.asyncSearchDebounceTimer)
            }
            this.asyncSearchDebounceTimer = setTimeout(() => {
                this.loadAsyncOptions(true)
            }, 350)
        },

        onOptionListScroll(event) {
            if (!this.isAsyncSelectType || this.asyncLoading || !this.asyncHasMore) {
                return
            }
            const el = event.target
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
                this.loadAsyncOptions(false)
            }
        },

        loadAsyncOptions(reset) {
            const field = this.activeField
            if (!field || typeof field.loadOptions !== 'function') {
                return
            }

            if (reset) {
                this.asyncPage = 1
                this.asyncOptions = []
                this.asyncHasMore = false
            }

            const page = reset ? 1 : this.asyncPage
            this.asyncLoading = true

            Promise.resolve(field.loadOptions({
                query: this.optionSearch.trim(),
                page,
                perPage: 25,
            }))
                .then((result) => {
                    const options = (result && result.options) ? result.options : []
                    options.forEach((option) => {
                        this.$set(this.optionLabelCache, String(option.id), option.label)
                    })

                    if (reset) {
                        this.asyncOptions = options
                    } else {
                        const existing = new Set(this.asyncOptions.map((item) => String(item.id)))
                        const merged = [...this.asyncOptions]
                        options.forEach((option) => {
                            if (!existing.has(String(option.id))) {
                                merged.push(option)
                            }
                        })
                        this.asyncOptions = merged
                    }

                    this.asyncHasMore = Boolean(result && result.hasMore)
                    this.asyncPage = page + 1
                })
                .catch(() => {
                    if (reset) {
                        this.asyncOptions = []
                    }
                    this.asyncHasMore = false
                })
                .finally(() => {
                    this.asyncLoading = false
                })
        },

        cancelEdit() {
            this.activeFieldKey = null
            this.resetDraft()
            this.fieldMenuOpen = false
        },

        setDraftOperator(operator) {
            this.draftOperator = operator === 'is_not' ? 'is_not' : 'is'
            if (this.activeFieldType === 'date') {
                if (hasFilterValue({
                    type: 'date',
                    preset: this.draftPreset,
                    from: this.draftFrom,
                    to: this.draftTo,
                }, 'date')) {
                    this.commitDateDraft()
                }
                return
            }
            if (this.activeFieldType === 'text') {
                const value = this.optionSearch.trim()
                if (value) {
                    this.emitChange(this.activeFieldKey, {
                        type: 'text',
                        value,
                        operator: this.draftOperator,
                    })
                }
                return
            }
            if (this.draftValues.length) {
                this.commitSelectDraft()
            }
        },

        selectDatePreset(presetId) {
            this.draftPreset = presetId
            if (presetId !== 'custom_date' && presetId !== 'custom_range') {
                this.draftFrom = ''
                this.draftTo = ''
                this.commitDateDraft()
                return
            }
            if (presetId === 'custom_date') {
                this.draftTo = ''
                this.focusDraftInput()
                if (this.draftFrom) {
                    this.commitDateDraft()
                }
                return
            }
            this.focusDraftInput()
            if (this.draftFrom || this.draftTo) {
                this.commitDateDraft()
            }
        },

        onCustomDateInput(value) {
            this.draftFrom = value
            this.draftTo = value
            this.scheduleDateCommit()
        },

        onCustomRangeFromInput(value) {
            this.draftFrom = value
            this.scheduleDateCommit()
        },

        onCustomRangeToInput(value) {
            this.draftTo = value
            this.scheduleDateCommit()
        },

        scheduleDateCommit() {
            if (this.rangeDebounceTimer) {
                clearTimeout(this.rangeDebounceTimer)
            }
            this.rangeDebounceTimer = setTimeout(() => {
                this.commitDateDraft()
            }, 300)
        },

        commitDateDraft() {
            if (!this.activeFieldKey) {
                return
            }
            this.emitChange(this.activeFieldKey, {
                type: 'date',
                preset: this.draftPreset || 'custom_range',
                from: this.draftFrom,
                to: this.draftPreset === 'custom_date' ? this.draftFrom : this.draftTo,
                operator: this.draftOperator === 'is_not' ? 'is_not' : 'is',
            })
        },

        toggleDraftValue(id, checked) {
            const next = [...this.draftValues]
            const index = next.findIndex((value) => String(value) === String(id))

            if (checked && index === -1) {
                next.push(id)
                const option = this.displayedOptions.find((item) => String(item.id) === String(id))
                if (option) {
                    this.$set(this.optionLabelCache, String(id), option.label)
                }
            } else if (!checked && index !== -1) {
                next.splice(index, 1)
            }

            this.draftValues = next
            this.commitSelectDraft()
        },

        onRangeFromInput(value) {
            this.draftFrom = value
            this.scheduleRangeCommit()
        },

        onRangeToInput(value) {
            this.draftTo = value
            this.scheduleRangeCommit()
        },

        scheduleRangeCommit() {
            if (this.rangeDebounceTimer) {
                clearTimeout(this.rangeDebounceTimer)
            }
            this.rangeDebounceTimer = setTimeout(() => {
                this.commitRangeDraft()
            }, 300)
        },

        commitSelectDraft() {
            if (!this.activeFieldKey) {
                return
            }
            const type = this.isAsyncSelectType ? 'async-select' : 'select'
            const valueLabels = {}
            this.draftValues.forEach((id) => {
                const key = String(id)
                if (this.optionLabelCache[key]) {
                    valueLabels[key] = this.optionLabelCache[key]
                }
            })
            this.emitChange(this.activeFieldKey, {
                type,
                values: [...this.draftValues],
                operator: this.draftOperator === 'is_not' ? 'is_not' : 'is',
                valueLabels,
            })
        },

        commitRangeDraft() {
            if (!this.activeFieldKey) {
                return
            }
            this.emitChange(this.activeFieldKey, {
                type: 'number',
                from: this.draftFrom,
                to: this.draftTo,
                operator: 'between',
            })
        },

        removeField(key) {
            const field = this.fields.find((item) => item.key === key)
            const type = (field && field.type) || 'select'
            this.emitChange(key, emptyStateForType(type))
            if (this.activeFieldKey === key) {
                this.cancelEdit()
            }
        },

        emitChange(key, nextState) {
            const next = { ...this.value }
            Object.keys(next).forEach((existingKey) => {
                next[existingKey] = { ...next[existingKey] }
            })
            next[key] = { ...nextState }
            this.$emit('input', next)
            this.$emit('change', next)
        },
    },
}
</script>

<style lang="scss" scoped>
.shopify-filter-bar {
    width: 100%;
}

.shopify-filter-bar__control {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem;
    min-height: 36px;
    padding: 0.25rem 0.4rem;
    border: 1px solid #ced4da;
    border-radius: 0.5rem;
    background: #fff;
    cursor: text;
}

.shopify-filter-bar__search-icon {
    color: #6c757d;
    padding: 0 0.15rem;
    font-size: 0.85rem;
}

.shopify-filter-bar__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    max-width: 100%;
    padding: 0.15rem 0.35rem;
    border-radius: 0.35rem;
    background: #f1f3f5;
    font-size: 0.8125rem;
    cursor: pointer;
}

.shopify-filter-bar__pill-label,
.shopify-filter-bar__pill-op {
    color: #495057;
    white-space: nowrap;
}

.shopify-filter-bar__pill-value {
    display: inline-block;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0 0.35rem;
    border-radius: 0.25rem;
    background: #5c6ac4;
    color: #fff;
}

.shopify-filter-bar__pill-remove {
    border: 0;
    background: transparent;
    color: #6c757d;
    font-size: 1rem;
    line-height: 1;
    padding: 0 0.15rem;
    cursor: pointer;

    &:hover {
        color: #212529;
    }
}

.shopify-filter-bar__add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid #c9cccf;
    border-radius: 999px;
    background: #fff;
    color: #5c5f62;
    font-size: 0.7rem;
    cursor: pointer;

    &:hover {
        background: #f6f6f7;
        color: #202223;
    }
}

.shopify-filter-bar__input-wrap {
    position: relative;
    display: flex;
    flex: 1 1 160px;
    align-items: center;
    min-width: 140px;
    min-height: 28px;
    gap: 0.35rem;
    cursor: text;
}

.shopify-filter-bar__draft {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.875rem;
    white-space: nowrap;
    color: #202223;
}

.shopify-filter-bar__draft-label,
.shopify-filter-bar__draft-op {
    color: #6d7175;
}

.shopify-filter-bar__draft-spacer {
    flex: 1 1 auto;
    min-width: 40px;
}

.shopify-filter-bar__input {
    flex: 1 1 auto;
    min-width: 80px;
    width: 100%;
    border: 0;
    outline: none;
    box-shadow: none;
    background: transparent;
    font-size: 0.875rem;
    padding: 0.15rem 0;

    &::-webkit-search-cancel-button {
        display: none;
    }
}

.shopify-filter-bar__input--draft {
    flex: 0 1 120px;
    min-width: 40px;
    max-width: 160px;
    width: auto;
}

.shopify-filter-bar__menu {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 1050;
    min-width: 240px;
    max-width: 320px;
    max-height: 320px;
    overflow: auto;
    padding: 0.35rem 0;
    border: 1px solid #dfe3e8;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 0 0.5rem 1.25rem rgba(0, 0, 0, 0.12);
}

.shopify-filter-bar__menu--values,
.shopify-filter-bar__menu--range,
.shopify-filter-bar__menu--date,
.shopify-filter-bar__menu--text {
    min-width: 280px;
    padding: 0.5rem;
    overflow: visible;
}

.shopify-filter-bar__date-preset {
    display: flex !important;
    align-items: center;
    gap: 0.5rem;

    i {
        width: 0.85rem;
        color: #5c6ac4;
    }

    &.is-active {
        background: #f6f6f7;
    }
}

.shopify-filter-bar__check-spacer {
    display: inline-block;
    width: 0.85rem;
}

.shopify-filter-bar__custom-date {
    border-top: 1px solid #e1e3e5;
    margin-top: 0.35rem;
    padding-top: 0.5rem;
    margin-bottom: 0.35rem;

    label {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.75rem;
        color: #6d7175;
        margin: 0;
    }
}

.shopify-filter-bar__menu-item {
    display: block;
    width: 100%;
    border: 0;
    background: transparent;
    text-align: left;
    padding: 0.55rem 0.85rem;
    font-size: 0.875rem;
    color: #202223;
    cursor: pointer;

    &:hover {
        background: #f6f6f7;
    }
}

.shopify-filter-bar__menu-empty {
    padding: 0.55rem 0.85rem;
    color: #6d7175;
    font-size: 0.8125rem;
}

.shopify-filter-bar__option-list {
    max-height: 240px;
    overflow: auto;
    margin-bottom: 0.35rem;
}

.shopify-filter-bar__option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.4rem 0.35rem;
    font-size: 0.875rem;
    cursor: pointer;

    input {
        margin: 0;
    }

    &:hover {
        background: #f6f6f7;
        border-radius: 0.25rem;
    }
}

.shopify-filter-bar__operators {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    border-top: 1px solid #e1e3e5;
    padding-top: 0.35rem;
}

.shopify-filter-bar__operator {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    width: 100%;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    text-align: left;
    padding: 0.45rem 0.4rem;
    font-size: 0.875rem;
    cursor: pointer;

    i {
        width: 0.85rem;
        color: #5c6ac4;
    }

    &:hover,
    &.is-active {
        background: #f6f6f7;
    }
}

.shopify-filter-bar__range-row {
    display: flex;
    align-items: flex-end;
    gap: 0.5rem;

    label {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        font-size: 0.75rem;
        color: #6d7175;
        margin: 0;
        flex: 1;
    }
}

.shopify-filter-bar__range-input {
    width: 100%;
    border: 1px solid #c9cccf;
    border-radius: 0.25rem;
    padding: 0.35rem 0.45rem;
    font-size: 0.875rem;
}

.shopify-filter-bar__range-sep {
    padding-bottom: 0.45rem;
    color: #6d7175;
}

.shopify-filter-bar__text-hint {
    margin-top: 0.35rem;
    font-size: 0.75rem;
    color: #6d7175;
}

.shopify-filter-bar__clear-x {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-left: auto;
    border: 1px solid #c9cccf;
    border-radius: 999px;
    background: #fff;
    color: #5c5f62;
    font-size: 0.7rem;
    cursor: pointer;

    &:hover {
        background: #f6f6f7;
        color: #202223;
    }
}
</style>
